<template>
  <LlConfirmationDialog
    ref="dialogRef"
    title="Choose what should be done with the unbilled time entries"
    :message="message"
    confirm-text="Lock time"
    confirm-color="primary"
    confirm-variant="flat"
    :max-width="600"
    persistent
    :confirm-disabled="!choice"
    data-cy="wip-disposition-dialog"
  >
    <template #content>
      <LlRadioGroup
        v-model="choice"
        :options="options"
        cards
        data-cy="wip-disposition-choice"
      />
    </template>
  </LlConfirmationDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlRadioGroup from '@/components/LlRadioGroup.vue';
import { WipDisposition } from '../constants';

/**
 * UI-008 (one fee) and UI-009 (a bulk selection) are the same prompt with
 * different copy, so they are one component: the only differences are the
 * sentence and the plural.
 *
 * The choice is mandatory (BR-012) — Confirm stays disabled until one is picked,
 * and there is no default. Cancelling resolves null and the caller makes no
 * request at all, leaving every fee Billed and Time Unlocked.
 */
const dialogRef = ref<{ open: () => Promise<boolean> } | null>(null);
const choice = ref<string | null>(null);
const feeCount = ref(0);
const entryCount = ref(0);

const isBulk = computed(() => feeCount.value > 1);

const message = computed(() => {
  const entries = `${entryCount.value} time ${
    entryCount.value === 1 ? 'entry' : 'entries'
  } not yet billed`;
  // UI-009 states the consequence first, then the aggregate (frd.md §3.3) — a
  // bulk action touching specific entries has to say why before it asks what.
  return isBulk.value
    ? `Locking time on these ${feeCount.value} fees will affect time entries that aren't yet billed. Together they have ${entries}.`
    : `This fee has ${entries}.`;
});

const options = computed(() => [
  {
    label: isBulk.value ? 'Mark them all Billed now' : 'Mark them Billed now',
    value: WipDisposition.MarkBilled,
    // The irreversibility line is required by the story and must not be
    // simplified away: these entries stay Billed through a later Unlock Time.
    description:
      "Once billed, these entries can't be moved to another fee — including if you unlock this fee later.",
    descriptionClass: 'text-error',
    dataCy: 'wip-mark-billed',
  },
  {
    label: isBulk.value
      ? 'Leave them all in the workflow'
      : 'Leave them in the workflow',
    value: WipDisposition.LeaveInWorkflow,
    description:
      'They stay unbilled and appear on Ready to Bill by their own entry date.',
    dataCy: 'wip-leave-in-workflow',
  },
]);

/** Resolves the chosen disposition, or null when cancelled. */
async function open(counts: { feeCount: number; entryCount: number }) {
  feeCount.value = counts.feeCount;
  entryCount.value = counts.entryCount;
  choice.value = null;

  try {
    const confirmed = await dialogRef.value?.open();
    return confirmed ? choice.value : null;
  } catch {
    // Treated as a cancel: no disposition means the caller makes no request,
    // which is the safe end of a prompt whose whole job is to be answered.
    return null;
  }
}

defineExpose({ open });
</script>
